import { ContentItem, ContextData } from '@/core/ambient-intelligence/types/ambient-intelligence.types';

import { ContextExtractor, ContextRegistry } from '../components/registries/ContextRegistry';

// Blog Inspect View Context Extractor
class BlogInspectContextExtractor implements ContextExtractor {
  extract(pageData: any): ContextData {
    const content: ContentItem = pageData?.content;

    return {
      type: 'blog_inspect',
      contentId: content?.id,
      contentType: 'blog',
      confidence: 0.95,
      metadata: {
        title: content?.title,
        author: content?.source?.author?.name,
        tags: content?.tags || [],
        categories: content?.categories || [],
        readingTime: content?.data?.readingTime,
        topic: content?.tags?.[0] || 'general',
        engagement: content?.engagement,
        emotionalTone: content?.insights?.emotionalTone,
        relatedTopics: this.extractRelatedTopics(content),
        contentDepth: this.assessContentDepth(content),
      },
    };
  }

  canExtract(pageType: string): boolean {
    return pageType === 'blog_inspect';
  }

  private extractRelatedTopics(content: ContentItem): string[] {
    const topics = new Set<string>();

    // Add main tags
    content?.tags?.forEach((tag) => topics.add(tag));

    // Add categories
    content?.categories?.forEach((cat) => topics.add(cat.toLowerCase()));

    // Extract topics from title
    const titleWords = content?.title?.toLowerCase().split(' ') || [];
    const topicKeywords = ['design', 'development', 'marketing', 'ai', 'technology', 'business'];
    titleWords.forEach((word) => {
      if (topicKeywords.includes(word)) {
        topics.add(word);
      }
    });

    return Array.from(topics);
  }

  private assessContentDepth(content: ContentItem): string {
    const wordCount = content?.data?.wordCount || 0;
    const sections = content?.data?.sections?.length || 0;

    if (wordCount > 2000 || sections > 5) return 'comprehensive';
    if (wordCount > 1000 || sections > 3) return 'detailed';
    return 'overview';
  }
}

// Product Inspect View Context Extractor
class ProductInspectContextExtractor implements ContextExtractor {
  extract(pageData: any): ContextData {
    const content: ContentItem = pageData?.content;

    return {
      type: 'product_inspect',
      contentId: content?.id,
      contentType: 'product',
      confidence: 0.95,
      metadata: {
        productName: content?.title,
        price: content?.data?.price,
        currency: content?.data?.currency,
        category: content?.categories?.[0],
        tags: content?.tags || [],
        inStock: content?.data?.inStock,
        rating: content?.engagement?.rating,
        features: content?.data?.features,
        specifications: content?.data?.specifications,
        priceRange: this.determinePriceRange(content?.data?.price),
        productType: this.detectProductType(content),
        similarCategories: this.findSimilarCategories(content),
      },
    };
  }

  canExtract(pageType: string): boolean {
    return pageType === 'product_inspect';
  }

  private determinePriceRange(price?: number): string {
    if (!price) return 'unknown';
    if (price < 50) return 'budget';
    if (price < 200) return 'mid-range';
    if (price < 500) return 'premium';
    return 'luxury';
  }

  private detectProductType(content: ContentItem): string {
    const title = content?.title?.toLowerCase() || '';
    const tags = content?.tags?.join(' ').toLowerCase() || '';

    if (title.includes('phone') || title.includes('laptop')) return 'electronics';
    if (title.includes('shirt') || title.includes('dress')) return 'fashion';
    if (title.includes('book') || title.includes('course')) return 'educational';
    if (tags.includes('home') || tags.includes('furniture')) return 'home';

    return content?.categories?.[0]?.toLowerCase() || 'general';
  }

  private findSimilarCategories(content: ContentItem): string[] {
    const categories = new Set<string>();

    // Add main category
    if (content?.categories?.[0]) {
      categories.add(content.categories[0]);
    }

    // Add related based on product type
    const productType = this.detectProductType(content);
    const relatedMap: Record<string, string[]> = {
      electronics: ['accessories', 'gadgets', 'smart home'],
      fashion: ['accessories', 'shoes', 'jewelry'],
      home: ['decor', 'furniture', 'kitchen'],
      educational: ['courses', 'tutorials', 'guides'],
    };

    relatedMap[productType]?.forEach((cat) => categories.add(cat));

    return Array.from(categories);
  }
}

// Image Inspect View Context Extractor
class ImageInspectContextExtractor implements ContextExtractor {
  extract(pageData: any): ContextData {
    const content: ContentItem = pageData?.content;

    return {
      type: 'image_inspect',
      contentId: content?.id,
      contentType: 'image',
      confidence: 0.95,
      metadata: {
        title: content?.title,
        photographer: content?.source?.author?.name,
        tags: content?.tags || [],
        categories: content?.categories || [],
        dimensions: content?.data?.dimensions,
        format: content?.data?.format,
        location: content?.data?.location,
        camera: content?.data?.camera,
        imageStyle: this.detectImageStyle(content),
        subject: this.identifySubject(content),
        mood: this.analyzeMood(content),
        technicalLevel: this.assessTechnicalLevel(content?.data),
      },
    };
  }

  canExtract(pageType: string): boolean {
    return pageType === 'image_inspect';
  }

  private detectImageStyle(content: ContentItem): string {
    const tags = content?.tags?.join(' ').toLowerCase() || '';
    const title = content?.title?.toLowerCase() || '';

    if (tags.includes('portrait') || title.includes('portrait')) return 'portrait';
    if (tags.includes('landscape') || title.includes('landscape')) return 'landscape';
    if (tags.includes('abstract')) return 'abstract';
    if (tags.includes('street')) return 'street';
    if (tags.includes('architecture')) return 'architecture';
    if (tags.includes('nature')) return 'nature';

    return 'general';
  }

  private identifySubject(content: ContentItem): string {
    const title = content?.title?.toLowerCase() || '';
    const tags = content?.tags || [];

    // Check common subjects
    const subjects = ['people', 'nature', 'architecture', 'food', 'animals', 'technology', 'art'];
    for (const subject of subjects) {
      if (title.includes(subject) || tags.some((tag) => tag.toLowerCase().includes(subject))) {
        return subject;
      }
    }

    return tags[0] || 'general';
  }

  private analyzeMood(content: ContentItem): string {
    const description = content?.description?.toLowerCase() || '';
    const title = content?.title?.toLowerCase() || '';

    const moods = {
      serene: ['peaceful', 'calm', 'tranquil', 'quiet'],
      vibrant: ['colorful', 'bright', 'lively', 'energetic'],
      dramatic: ['powerful', 'intense', 'bold', 'striking'],
      nostalgic: ['vintage', 'classic', 'timeless', 'old'],
      modern: ['contemporary', 'minimal', 'clean', 'sleek'],
    };

    for (const [mood, keywords] of Object.entries(moods)) {
      if (keywords.some((keyword) => description.includes(keyword) || title.includes(keyword))) {
        return mood;
      }
    }

    return 'neutral';
  }

  private assessTechnicalLevel(data: any): string {
    // Assess based on camera settings if available
    if (data?.aperture && data?.iso && data?.shutterSpeed) {
      return 'professional';
    }
    if (data?.camera) {
      return 'enthusiast';
    }
    return 'casual';
  }
}

// Register all inspect view extractors
export function registerInspectViewContextExtractors() {
  ContextRegistry.register('blog_inspect', new BlogInspectContextExtractor());
  ContextRegistry.register('product_inspect', new ProductInspectContextExtractor());
  ContextRegistry.register('image_inspect', new ImageInspectContextExtractor());

  console.log('✅ Inspect view context extractors registered');
}

// Auto-register on import
registerInspectViewContextExtractors();
